import React, { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import DataTable, { DataTableColumn } from '../../design/DataTable';
import PnlText from '../../design/PnlText';
import TickerLink from '../TickerLink';
import { PerTickerRow } from '../../hooks/useStatisticsPanels';

interface PerTickerTableProps {
  rows: PerTickerRow[] | null;
  loading: boolean;
}

type SortField = 'ticker' | 'trades' | 'win_rate' | 'total_pnl' | 'avg_pnl' | 'avg_hold_seconds';

const TOP_N = 10;

export const formatAvgHold = (seconds: number | null): string => {
  if (seconds === null) return '—';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 48 * 3600) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
};

const COLUMNS: DataTableColumn<PerTickerRow>[] = [
  {
    key: 'ticker', label: 'Ticker', sortable: true,
    render: (r) => <TickerLink ticker={r.ticker} />,
  },
  { key: 'trades', label: 'Trades', sortable: true, align: 'right', render: (r) => r.trades },
  { key: 'win_rate', label: 'Win rate', sortable: true, align: 'right', render: (r) => `${r.win_rate.toFixed(1)}%` },
  {
    key: 'total_pnl', label: 'Total P&L', sortable: true, align: 'right',
    render: (r) => <PnlText value={r.total_pnl} format="currency" variant="body2" />,
  },
  {
    key: 'avg_pnl', label: 'Avg P&L', sortable: true, align: 'right',
    render: (r) => <PnlText value={r.avg_pnl} format="currency" variant="body2" />,
  },
  {
    key: 'avg_hold_seconds', label: 'Avg hold', sortable: true, align: 'right',
    render: (r) => formatAvgHold(r.avg_hold_seconds),
  },
];

const PerTickerTable: React.FC<PerTickerTableProps> = ({ rows, loading }) => {
  const [sortBy, setSortBy] = useState<SortField>('total_pnl');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() => {
    return [...(rows ?? [])].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      // Nulls (avg_hold before any closed trade) always sink to the end.
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      let cmp: number;
      if (sortBy === 'ticker') cmp = (av as string).localeCompare(bv as string);
      else cmp = (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortBy, sortDir]);

  const total = sorted.length;
  const visibleRows = expanded ? sorted : sorted.slice(0, TOP_N);

  const handleSort = (key: string) => {
    const field = key as SortField;
    const isAsc = sortBy === field && sortDir === 'asc';
    setSortDir(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  return (
    <Box>
      <DataTable
        columns={COLUMNS}
        rows={visibleRows}
        rowKey={(r) => r.ticker}
        loading={loading}
        skeletonRows={5}
        emptyMessage="No realized trades in this period"
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={handleSort}
      />
      {!loading && total > TOP_N && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Button size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Show top 10' : `Show all (${total})`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PerTickerTable;
